import { useEffect, useRef } from "react";

/**
 * One bar with three identities, so the bottom of the grid always says what
 * the next action is: nothing pending, rows selected, or changes unsaved.
 *
 * The bulk selects are pinned to the empty value, which resets them the moment
 * a choice lands on the selected rows — the control is an action, not a state.
 *
 * @param {object} props
 * @param {number} props.dirtyCount rows with unsaved edits, new rows included
 * @param {number} props.newCount
 * @param {number} props.selectedCount
 * @param {number} props.errorCount
 * @param {boolean} props.saving
 * @param {string} [props.savedMessage]
 * @param {string} [props.saveError]
 * @param {{ key: string, label: string, options: { value: string, label: string }[] }[]} props.bulkControls
 * @param {(key: string, value: string) => void} props.onBulk
 * @param {() => void} props.onClearSelection
 * @param {() => void} props.onDiscard
 * @param {() => void} props.onSave
 * @param {() => void} props.onDismissError
 * @param {React.ReactNode} [props.children] shown after the idle message, e.g. a link to a just-saved row
 */
export default function ActionBar({
  dirtyCount,
  newCount,
  selectedCount,
  errorCount,
  saving,
  savedMessage,
  saveError,
  bulkControls,
  onBulk,
  onClearSelection,
  onDiscard,
  onSave,
  onDismissError,
  children,
}) {
  const state = selectedCount > 0 ? "selection" : dirtyCount > 0 ? "unsaved" : "idle";
  const barRef = useRef(null);

  useEffect(() => {
    const bar = barRef.current;
    if (state === "selection" && typeof bar?.scrollIntoView === "function") {
      bar.scrollIntoView({ block: "nearest" });
    }
  }, [state]);

  return (
    <div className="kgb-actionbar" data-state={state} ref={barRef}>
      <p className="kgb-actionbar-message">
        {state === "selection" ? (
          <>
            <em>{selectedCount}</em> row{selectedCount === 1 ? "" : "s"} selected
          </>
        ) : null}
        {state === "unsaved" ? (
          <>
            <em>{dirtyCount}</em> row{dirtyCount === 1 ? "" : "s"} changed
            {newCount ? (
              <>
                {" · "}
                <em>{newCount}</em> new
              </>
            ) : null}
          </>
        ) : null}
        {state === "idle" ? (
          <span className={savedMessage ? "kgb-saved" : undefined}>
            {savedMessage || "All changes saved"}
          </span>
        ) : null}
        {state === "idle" ? children : null}
      </p>

      <div className="kgb-actionbar-spacer" />

      {saveError ? (
        <span className="kgb-actionbar-error">
          {saveError}
          <button type="button" className="kgb-btn kgb-btn--quiet" onClick={onDismissError}>
            Dismiss
          </button>
        </span>
      ) : null}

      {selectedCount > 0 ? (
        <div className="kgb-actionbar-group">
                    {bulkControls.map((control) => (
            <select
              key={control.key}
              className="kgb-bulk-select"
              value=""
              aria-label={control.label}
              onChange={(e) => onBulk(control.key, e.target.value)}
            >
              <option value="">{control.label}</option>
              {control.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ))}
          <button type="button" className="kgb-btn kgb-btn--quiet" onClick={onClearSelection}>
            Clear selection
          </button>
        </div>
      ) : null}

      {dirtyCount > 0 ? (
        <div className="kgb-actionbar-group">
          {errorCount > 0 ? (
            <span className="kgb-actionbar-blocked">
              {errorCount} row{errorCount === 1 ? "" : "s"} need fixing
            </span>
          ) : null}
          <button type="button" className="kgb-btn" disabled={saving} onClick={onDiscard}>
            Discard changes
          </button>
          <button
            type="button"
            className="kgb-btn kgb-btn--primary"
            disabled={saving || errorCount > 0}
            onClick={onSave}
          >
            {saving ? "Saving…" : "Save all changes"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
